'use client';

/**
 * R7 hallmark paper flip (L4.1, 2026-07-17): "merge into the paper world" —
 * this file itself renders no band/ink of its own (see the pre-existing
 * note below: `LightInterlude` owns the ONE `LightBand`), so there is no
 * dark-token-to-paper-token swap to make here — the "light island in a dark
 * sea" framing throughout this file's history is now simply "the page's own
 * paper," structurally unchanged. Two items are OUT of this file's reach and
 * flagged for integration/the primitive owner instead: (1) `SerifQuote`
 * (`ui-light/SerifQuote.tsx`) is not in L4's file ownership — the brief's
 * "may now be the LARGEST type moment on the page (t3 voice)" instruction
 * requires bumping that primitive's `clamp(30px,4.2vw,54px)` type scale,
 * which this file cannot do; (2) `LightInterlude`/`LightBand` (not L4-owned
 * either) still assign the exact paper band step this section's transparent
 * `<section>` renders onto.
 *
 * SerifQuoteSection — the light interlude's editorial pull-quote (R4-P3
 * masterplan §2.6, attio ref `chrome_oywJ3ivdyg`), as reshaped by
 * [AMENDED per B1/D3] ("delivers exactly one default export:
 * `LightInterlude.tsx`... renders two transparent semantic `<section>`
 * children: named `SerifQuoteSection` and `SocialProofSection`").
 *
 * This file renders ONLY the transparent `<section>` + the `SerifQuote`
 * primitive — it does NOT wrap itself in `LightBand`/paper background: the
 * masterplan is explicit that ONE `LightBand` + ONE baked dot layer covers
 * the whole interlude (a second independent paper wrapper here would
 * phase-jump the 96×96 dot tile at the quote→stats boundary). `LightInterlude`
 * (the single default-exported owner) provides the paper.
 *
 * Content: `LANDING_CONTENT.light.quote` (D1 closed manifest, P3-6a-owned).
 *
 * [CRITICAL-1 fix, r4-p3-review-plan.md]: this quote renders UNATTRIBUTED —
 * `SerifQuote`'s `attributionName` prop is NOT passed. The content-surgeon
 * FLAG originally left in `content.ts` (read in full before this file was
 * first written) proposed attributing `partners.trustLine` — CMG's own
 * second-person promise copy ("You will not get an empty CRM: we set it up
 * together with your team...") — to "Clinica de Marketing, founding
 * partner" via a `<figcaption>` byline. On review that is a fabricated
 * testimonial: the text is grammatically CMG talking about itself ("we"),
 * not something a customer said, and no approved personal Clinica
 * testimonial exists on disk (the actual ruling of record,
 * `r3-judge-C-verdict.md:233`, is "KILL: no approved factual content. Do
 * not revive `socialProof.quote`, `.author`, or `.metric`."). Rendering it
 * inside `SerifQuote`'s literal quotation-mark + byline grammar would have
 * presented CMG's own copy as if Clinica said it — the exact fabrication
 * Law #8 (TRUTH BARRIER) bans. `light.quote` no longer carries an
 * `attribution` key (see `content.ts`); this section renders the two-tone
 * clauses as an unattributed editorial statement instead.
 *
 * Motion: a quiet reveal on in-view — the quote fades/rises ≤12px
 * (`LANDING_MOTION_MS.enter`, `LANDING_EASE.reveal` per masterplan §2.6).
 * The reveal target is `SerifQuote`'s own semantic `<blockquote>` node,
 * queried by tag (a frozen `ui-light/**` primitive is COMPOSED, never
 * forked — Law #12 — so this reaches into its already-public, always-
 * present markup rather than adding new wrapper divs or editing the
 * primitive). No `<figcaption>` target: [CRITICAL-1 fix] this quote renders
 * unattributed, so `SerifQuote` never mounts a `<figcaption>` here. PRM =
 * static (the node renders at its default, fully-visible, zero-transform
 * state — the imperative "hide until reveal" write only ever happens
 * inside an effect, never in JSX/SSR markup, so a reduced-motion or no-JS
 * visitor never sees a blank slot).
 *
 * [MINOR fix, r4-p3-review-correctness.md]: the initial hide write runs in
 * `useLayoutEffect` (before paint), split from the `inView` subscription +
 * reveal animation in `useEffect` (after paint) — a visitor already
 * in-viewport at mount (deep link to `#promisiune`, a tall/zoomed-out
 * viewport, a fast initial scroll) would otherwise see the SSR-rendered,
 * fully-visible quote paint for one frame before the hide write applied,
 * then re-reveal moments later — a flash. `ProductStoryMotionController.tsx`
 * documents the identical principle for its own reveal ("the just-revealed
 * panel must never flash its fully-drawn end-state before the run arms ...
 * `reset` has to run before paint").
 *
 * `SERIF_QUOTE_TIMING` is this scene's ONE frozen timing table
 * (`check-landing-choreography.mjs` auto-discovers any `*_TIMING`-suffixed
 * const, [AMENDED per A4] — no separate registration).
 */

import { useEffect, useLayoutEffect, useRef } from 'react';
import { inView } from 'motion';
import { useLocale } from '../hooks/useLocale';
import { useReducedMotion } from '../hooks/useMediaQuery';
import { LANDING_CONTENT } from '../constants/content';
import { LANDING_MOTION_MS, LANDING_EASE_CSS, LANDING_INVIEW } from '../motion/landing-motion';
import { SerifQuote } from '../ui-light/SerifQuote';

/** This scene's ONE frozen timing table (Harmonization f / [AMENDED per A4]). */
export const SERIF_QUOTE_TIMING = Object.freeze({
  inViewAmount: LANDING_INVIEW.entrance.amount,
  inViewMargin: LANDING_INVIEW.entrance.margin,
  riseDurationMs: LANDING_MOTION_MS.enter,
  riseTravelPx: 12,
  riseEase: LANDING_EASE_CSS.reveal,
});

export default function SerifQuoteSection() {
  const { t } = useLocale();
  const prefersReducedMotion = useReducedMotion();
  const sectionRef = useRef<HTMLElement | null>(null);

  const { light } = LANDING_CONTENT;

  // Runs BEFORE paint — see the "MINOR fix" doc note above.
  useLayoutEffect(() => {
    if (prefersReducedMotion) return;
    const quoteEl = sectionRef.current?.querySelector<HTMLElement>('blockquote');
    if (!quoteEl) return;
    quoteEl.style.opacity = '0';
    quoteEl.style.transform = `translateY(${SERIF_QUOTE_TIMING.riseTravelPx}px)`;
  }, [prefersReducedMotion]);

  useEffect(() => {
    if (prefersReducedMotion) return;
    const root = sectionRef.current;
    if (!root) return;
    const quoteEl = root.querySelector<HTMLElement>('blockquote');
    if (!quoteEl) return;

    let fired = false;
    let running: Animation | null = null;

    const commitAndRelease = (anim: Animation) => {
      running = anim;
      anim.finished
        .then(() => {
          anim.commitStyles();
          anim.cancel();
        })
        .catch(() => {
          // Cancelled on unmount before finish — nothing to commit.
        });
    };

    const stop = inView(
      root,
      () => {
        if (fired) return;
        fired = true;

        if (typeof document !== 'undefined' && document.hidden) {
          quoteEl.style.opacity = '1';
          quoteEl.style.transform = 'none';
          return;
        }

        commitAndRelease(
          quoteEl.animate(
            [
              { opacity: 0, transform: `translateY(${SERIF_QUOTE_TIMING.riseTravelPx}px)` },
              { opacity: 1, transform: 'none' },
            ],
            {
              duration: SERIF_QUOTE_TIMING.riseDurationMs,
              easing: SERIF_QUOTE_TIMING.riseEase,
              fill: 'forwards',
            }
          )
        );
      },
      { amount: SERIF_QUOTE_TIMING.inViewAmount, margin: SERIF_QUOTE_TIMING.inViewMargin }
    );

    return () => {
      stop();
      try {
        running?.cancel();
      } catch {
        // Already finished/cancelled — nothing to undo.
      }
      quoteEl.style.opacity = '';
      quoteEl.style.transform = '';
    };
  }, [prefersReducedMotion]);

  return (
    <section
      ref={sectionRef}
      id="promisiune"
      // R6 rhythm (judge §2.9, three-beat interlude opening): the light
      // interlude opens on the quote and resolves downward through Growth --
      // a tight quote-bottom -> growth-top handoff, was py-16 md:py-24 (no lg
      // step).
      className="relative pt-20 pb-12 lg:pt-28 lg:pb-16 px-5 sm:px-6 lg:px-8 scroll-mt-24"
    >
      <SerifQuote
        leadClause={t(light.quote.leadClause)}
        restClause={t(light.quote.restClause)}
        surface="paper"
      />
    </section>
  );
}
